import { Button, Datepicker, Heading, useSnackbar } from 'component-library';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { type NewContainer } from '@sr/types';

import { getCountryCodeFromDwkey, isCustomContainerType, isPalletContainerType } from '@sr/utils';
import { InputField } from 'component-library/components/Input/Text/InputField';
import { useArticleGroups } from '#lib/hooks/useArticleGroups';
import { ContainerImage } from '../../ContainerImage';
import { MaterialPicker } from '../../MaterialPicker/MaterialPicker';
import { OtherLoadCarrierPicker } from '../../OtherLoadCarrierPicker/OtherLoadCarrierPicker';
import { ContainerPicker } from '../../ContainerPicker/ContainerPicker';

import {
  AddContainerWrapper,
  MaterialWrapper,
  MaterialAndImageWrapper,
  AmountAndDateWrapper,
  ButtonWrapper,
  DatePickerWrapper,
  AmountWrapper,
  ContainerImageWrapper,
} from './styles';

export const AddContainer = ({
  onSave,
  locationDwKey,
  container,
  handleAbortModal,
  useGrandParentLocation,
}: {
  onSave: (container: NewContainer) => void;
  locationDwKey: string;
  container?: NewContainer;
  handleAbortModal: () => void;
  useGrandParentLocation?: boolean;
}) => {
  const { t } = useTranslation();
  const [disableButton, setDisableButton] = useState(true);
  const [containerTypeValue, setContainerTypeValue] = useState<string>(
    container?.containerType ?? '',
  );
  const [containerName, setContainerName] = useState<string>(container?.containerName ?? '');
  const [otherLoadCarrierType, setOtherLoadCarrierType] = useState<string>(
    container?.otherLoadCarrierType ?? '',
  );
  const [otherLoadCarrierName, setOtherLoadCarrierName] = useState<string>(
    container?.otherLoadCarrierName ?? '',
  );
  const [material, setMaterial] = useState<string>(container?.material ?? '');
  const [materialOther, setMaterialOther] = useState<string>(container?.materialOther ?? '');
  const [materialName, setMaterialName] = useState<string>(container?.materialName ?? '');
  const [amount, setAmount] = useState<number | undefined>(container?.amount ?? 1);
  const [palletCollarAmount, setPalletCollarAmount] = useState<number>(
    container?.palletCollarAmount ?? 0,
  );

  const [date, setDate] = useState<Date | null>(
    container?.date ? dayjs(container.date).toDate() : null,
  );
  const { showSnackbar } = useSnackbar();
  const [country, bpid] = locationDwKey.split('_');
  const _locationDwKey = country + '_' + bpid;

  const { articles, isPending, error } = useArticleGroups({
    selectedBusinessPartners: [_locationDwKey],
    useGrandParentLocation,
  });

  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      value = '';
    }

    if (numericValue < 1) {
      value = '1';
    }

    if (numericValue > 100) {
      value = '100';
    }

    setAmount(value === '' ? undefined : Number(value));
  };

  const handleChangePalletCollarAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = Number(e.target.value) || 0;

    setPalletCollarAmount(Math.min(Math.max(numericValue, 0), 100));
  };

  const isPalletContainer = isPalletContainerType(containerTypeValue);
  const amountLabelKey = isPalletContainer
    ? 'rented-containers.add-containers.step1.add-container.modal.amount.pallet.label'
    : 'rented-containers.add-containers.step1.add-container.modal.amount.label';

  const handleSaveContainer = () => {
    container = {
      id: container?.id ? container.id : crypto.randomUUID(),
      containerType: containerTypeValue || '',
      containerName: containerName || '',
      otherLoadCarrierType: otherLoadCarrierType || '',
      otherLoadCarrierName: otherLoadCarrierName || '',
      material: material || '',
      materialName: materialName || '',
      materialOther: materialOther || '',
      amount: amount,
      palletCollarAmount: isPalletContainer ? palletCollarAmount : undefined,
      date: date ? dayjs(date).format('YYYY-MM-DD') : undefined,
    };
    onSave(container);
  };

  const saveLabel = container
    ? t('rented-containers.add-containers.step1.add-container.modal.button.update')
    : t('rented-containers.add-containers.step1.add-container.modal.button.add');

  useEffect(() => {
    let shouldEnable = false;
    const hasRequiredFieldsForOther = () => !!otherLoadCarrierType && !!material;

    const hasRequiredFields = () => !!material && !!amount && !!date;

    const isMaterialOtherValid = () => material !== 'other' || !!materialOther;

    if (containerTypeValue) {
      if (isCustomContainerType(containerTypeValue)) {
        shouldEnable = hasRequiredFieldsForOther() && isMaterialOtherValid();
      } else {
        shouldEnable = hasRequiredFields() && isMaterialOtherValid();
      }
    }

    setDisableButton(!shouldEnable);
  }, [amount, containerTypeValue, date, material, materialOther, otherLoadCarrierType]);

  if (error) {
    if (error.message.includes('Timeout')) {
      showSnackbar({
        severity: 'error',
        description: t('add-container.material-picker.components.message.error.timeout.text'),
      });
    } else {
      showSnackbar({
        severity: 'error',
        description: t('message.components.message.error.default.text'),
      });
    }
  }

  const datePicker = (
    <Datepicker
      excludeWeekends
      showHolidays
      holidaysCountryCode={getCountryCodeFromDwkey(_locationDwKey)}
      label={t('rented-containers.add-containers.step1.add-container.modal.date.label')}
      placeholderText={t(
        'rented-containers.add-containers.step1.add-container.modal.date.placeholder',
      )}
      onChange={(value) => {
        setDate(value);
      }}
      value={date ? dayjs(date).toDate().toString() : ''}
      selected={date}
      minDate={dayjs().toDate()}
      additionalInfoLabel={t(
        'rented-containers.add-containers.step1.add-container.modal.date.highlighted-dates-label',
      )}
      tooltip={t('rented-containers.add-containers.step1.add-container.modal.date.info-tooltip')}
    />
  );

  return (
    <AddContainerWrapper>
      <Heading as="h2" variant="medium">
        {t('rented-containers.add-containers.step1.add-container.modal.title')}
      </Heading>

      <MaterialPicker
        articles={articles}
        isPending={isPending}
        materialType={material}
        onChange={(type, name) => {
          setMaterial(type);
          setMaterialName(name);
        }}
      />
      {material === 'other' && (
        <InputField
          onChange={(event) => {
            setMaterialOther(event.target.value);
          }}
          defaultValue={materialOther}
          label={t(
            'rented-containers.add-containers.step1.add-container.modal.material.other.label',
          )}
          placeholder={t(
            'rented-containers.add-containers.step1.add-container.modal.material.other.placeholder',
          )}
        />
      )}

      <MaterialAndImageWrapper>
        <ContainerImageWrapper>
          <ContainerImage containerType={containerTypeValue} locationDwKey={_locationDwKey} />
        </ContainerImageWrapper>

        <MaterialWrapper>
          <ContainerPicker
            locationDwKey={_locationDwKey}
            onChange={(type, name) => {
              setContainerTypeValue(type);
              setContainerName(name);
            }}
            containerType={containerTypeValue}
            useGrandParentLocation={useGrandParentLocation}
          />
          {isCustomContainerType(containerTypeValue) && (
            <OtherLoadCarrierPicker
              containerType={otherLoadCarrierType}
              onChange={(type, name) => {
                setOtherLoadCarrierType(type);
                setOtherLoadCarrierName(name);
              }}
            />
          )}
          {!isCustomContainerType(containerTypeValue) && (
            <AmountAndDateWrapper stackDate={isPalletContainer}>
              <AmountWrapper>
                <InputField
                  label={t(amountLabelKey)}
                  type="number"
                  min={1}
                  onChange={handleChangeAmount}
                  value={amount ?? ''}
                  placeholder={t(
                    'rented-containers.add-containers.step1.add-container.modal.amount.placeholder',
                  )}
                />
              </AmountWrapper>
              {isPalletContainer && (
                <AmountWrapper>
                  <InputField
                    label={t(
                      'rented-containers.add-containers.step1.add-container.modal.pallet-collar-amount.label',
                    )}
                    type="number"
                    min={0}
                    onChange={handleChangePalletCollarAmount}
                    value={palletCollarAmount}
                    placeholder={t(
                      'rented-containers.add-containers.step1.add-container.modal.amount.placeholder',
                    )}
                  />
                </AmountWrapper>
              )}
              <DatePickerWrapper fullWidth={isPalletContainer}>{datePicker}</DatePickerWrapper>
            </AmountAndDateWrapper>
          )}
        </MaterialWrapper>
      </MaterialAndImageWrapper>

      <ButtonWrapper>
        <Button
          variant="tertiary"
          onClick={() => {
            handleAbortModal();
          }}
        >
          {t('rented-containers.add-containers.step1.add-container.modal.button.abort')}
        </Button>
        <Button
          disabled={disableButton}
          onClick={() => {
            handleSaveContainer();
          }}
        >
          {saveLabel}
        </Button>
      </ButtonWrapper>
    </AddContainerWrapper>
  );
};
